import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [ticket, setTicket] = useState(null);
  const [event, setEvent] = useState(null);

  const poll = useCallback(async (tries) => {
    if (tries >= MAX_TRIES) { setStatus("timeout"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        try {
          const t = await api.get(`/tickets/${sessionId}`);
          setTicket(t.data.ticket); setEvent(t.data.event);
        } catch (_) {}
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      setTimeout(() => poll(tries + 1), 2000);
    } catch (_) {
      setTimeout(() => poll(tries + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    poll(0);
  }, [sessionId, poll]);

  return (
    <>
      <Seo title="Order Confirmation" path="/payment/success" />
      <div className="min-h-screen flex items-center justify-center px-5 py-32 bg-transparent grain">
        <div className="card-lux p-10 md:p-14 max-w-lg w-full text-center relative z-[2]">
          {status === "checking" && (
            <><Loader2 size={52} className="text-neutral-900 mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-neutral-900">Confirming your payment…</h1>
            <p className="text-neutral-500 mt-3">Hang tight, this only takes a moment.</p></>
          )}
          {status === "paid" && (
            <><CheckCircle2 size={56} className="text-neutral-900 mx-auto mb-6" />
            <h1 className="font-serif text-4xl text-neutral-900">You're in!</h1>
            <p className="text-neutral-500 mt-3">Your tickets{event ? ` to ${event.title}` : ""} are confirmed. A confirmation has been sent to your email.</p>
            {ticket && (
              <div className="mt-8 bg-black/40 rounded-2xl p-6 border border-black/10">
                <p className="overline mb-2">Your Ticket · {ticket.tier_name} ×{ticket.quantity}</p>
                <p className="text-neutral-400 text-xs mt-2">Bring a valid photo ID — you're on the guest list under your name and email.</p>
              </div>
            )}
            <Link to="/events" className="btn-gold mt-8 inline-flex">Explore More Events</Link></>
          )}
          {(status === "failed" || status === "timeout") && (
            <><XCircle size={56} className="text-neonred mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-neutral-900">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-neutral-500 mt-3">{status === "timeout" ? "Your payment is taking longer than expected. If you were charged, your ticket will arrive by email shortly." : "We couldn't confirm your payment. Please try again."}</p>
            <Link to="/buy-tickets" className="btn-gold mt-8 inline-flex">Back to Events</Link></>
          )}
        </div>
      </div>
    </>
  );
}
